import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Filter, TrendingUp, Calendar, 
  Coins, Download, DollarSign, Percent, ExternalLink,
  CheckCircle, Clock, XCircle, AlertTriangle, Info
} from 'lucide-react';
import api from '../../lib/api';

interface YieldPayment {
  id: string;
  tokenId: string;
  tokenName: string;
  tokenSymbol: string;
  distributionId: string;
  quantity: number;
  yieldRate: number;
  grossAmount: number;
  taxWithheld: number;
  netAmount: number;
  paymentDate: string;
  status: 'PENDING' | 'PAID' | 'FAILED';
  ledgerEntryId?: string;
  failureReason?: string;
}

interface YieldSummary {
  totalReceived: number;
  totalTaxWithheld: number;
  totalGross: number;
  paymentsCount: number;
  avgYieldRate: number;
}

export function YieldHistory() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [expandedPayment, setExpandedPayment] = useState<string | null>(null);

  const { data: summary } = useQuery({
    queryKey: ['yield-summary', year],
    queryFn: async () => {
      const res = await api.get(`/tokenization/portfolio/yields/summary?year=${year}`);
      return res.data as YieldSummary;
    },
  });

  const { data: payments, isLoading } = useQuery({
    queryKey: ['yield-history', search, year],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('year', year);
      const res = await api.get(`/tokenization/portfolio/yields?${params}`);
      return res.data as YieldPayment[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/banking/tokens/portfolio')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Histórico de Rendimentos</h1>
          <p className="text-surface-400">Acompanhe os rendimentos recebidos dos seus tokens</p>
        </div>
        <button
          onClick={() => window.open(`/api/tokenization/portfolio/yields/export?year=${year}`, '_blank')}
          className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Exportar
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.totalReceived)}</p>
                <p className="text-sm text-surface-400">Total Líquido Recebido</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalGross)}</p>
                <p className="text-sm text-surface-400">Total Bruto</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Percent className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(summary.totalTaxWithheld)}</p>
                <p className="text-sm text-surface-400">IR Retido na Fonte</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/20 rounded-lg">
                <Calendar className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.paymentsCount}</p>
                <p className="text-sm text-surface-400">Pagamentos em {year}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por token..."
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
            />
          </div>
        </div>

        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Payments Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-surface-400">Carregando rendimentos...</p>
          </div>
        ) : !payments?.length ? (
          <div className="p-8 text-center">
            <TrendingUp className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400">Nenhum rendimento encontrado em {year}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Token</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Quantidade</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Taxa</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Bruto</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">IR Retido</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Líquido</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Data</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Status</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Crédito</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {payments.map((payment) => (
                <>
                  <tr 
                    key={payment.id} 
                    className={`hover:bg-surface-800/50 transition-colors ${payment.failureReason ? 'cursor-pointer' : ''}`}
                    onClick={() => payment.failureReason && setExpandedPayment(expandedPayment === payment.id ? null : payment.id)}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
                          <Coins className="w-4 h-4 text-primary-400" />
                        </div>
                        <div>
                          <p className="font-medium">{payment.tokenName}</p>
                          <p className="text-sm text-surface-400">{payment.tokenSymbol}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {payment.quantity.toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-4 text-right text-green-400">
                      {payment.yieldRate.toFixed(2)}%
                    </td>
                    <td className="px-4 py-4 text-right">
                      {formatCurrency(payment.grossAmount)}
                    </td>
                    <td className="px-4 py-4 text-right text-red-400">
                      -{formatCurrency(payment.taxWithheld)}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-green-400">
                      {formatCurrency(payment.netAmount)}
                    </td>
                    <td className="px-4 py-4 text-sm text-surface-400">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {payment.status === 'PAID' && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            <CheckCircle className="w-3 h-3" />
                            Pago
                          </span>
                        )}
                        {payment.status === 'PENDING' && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                            <Clock className="w-3 h-3" />
                            Pendente
                          </span>
                        )}
                        {payment.status === 'FAILED' && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                            <XCircle className="w-3 h-3" />
                            Falhou
                            {payment.failureReason && (
                              <Info className="w-3 h-3 ml-1" />
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {payment.ledgerEntryId ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/banking/statement?entry=${payment.ledgerEntryId}`);
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 transition-colors"
                          title="Ver no extrato"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {payment.ledgerEntryId.slice(0, 8)}...
                        </button>
                      ) : payment.status === 'PENDING' ? (
                        <span className="text-xs text-surface-500">Aguardando</span>
                      ) : payment.status === 'FAILED' ? (
                        <span className="text-xs text-red-400">Não creditado</span>
                      ) : (
                        <span className="text-xs text-surface-500">-</span>
                      )}
                    </td>
                  </tr>
                  {/* Expanded row for failure reason */}
                  {expandedPayment === payment.id && payment.failureReason && (
                    <tr key={`${payment.id}-details`} className="bg-red-500/5">
                      <td colSpan={9} className="px-4 py-3">
                        <div className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-red-400">Motivo da Falha:</p>
                            <p className="text-surface-300 mt-1">{payment.failureReason}</p>
                            <p className="text-xs text-surface-500 mt-2">
                              Entre em contato com o suporte se o problema persistir.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tax Info */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
        <h3 className="font-semibold mb-2">Informações Fiscais</h3>
        <p className="text-sm text-surface-400">
          Os rendimentos de tokens são tributados na fonte conforme a legislação vigente. 
          O IR retido é calculado automaticamente e pode ser compensado na declaração anual.
          Para mais informações, consulte seu contador ou acesse o informe de rendimentos.
        </p>
      </div>
    </div>
  );
}

export default YieldHistory;
